import { parseColoredText, stripColorTags } from '../utils/parseColoredText';
import {
  PrimaryClockIcon,
  PrimaryGiftIcon,
  SubClockIcon,
  SubGiftIcon,
} from '../icons/TierIcons';

interface TierCardProps {
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  variant: 'current' | 'next' | 'previous';
  borderGradient?: string;
  onLearnMore?: () => void;
}

const AMBIENT_GRADIENT_CURRENT =
  'linear-gradient(210deg, rgba(139,0,255,0.2), transparent)';

const LOCK_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ display: 'block', flexShrink: 0 }}>
    <path
      d="M17 10V8A5 5 0 0 0 7 8v2M12 14.5v2M8.8 21h6.4c1.68 0 2.52 0 3.16-.33a3 3 0 0 0 1.31-1.31C20 18.72 20 17.88 20 16.2v-.4c0-1.68 0-2.52-.33-3.16a3 3 0 0 0-1.31-1.31C17.72 11 16.88 11 15.2 11H8.8c-1.68 0-2.52 0-3.16.33a3 3 0 0 0-1.31 1.31C4 13.28 4 14.12 4 15.8v.4c0 1.68 0 2.52.33 3.16a3 3 0 0 0 1.31 1.31C6.28 21 7.12 21 8.8 21Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function BenefitLine({
  icon,
  text,
  textColor,
  useColorTags,
}: {
  icon: React.ReactNode;
  text: string | undefined;
  textColor: string;
  useColorTags: boolean;
}) {
  if (!text) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
      <div style={{ flexShrink: 0, marginTop: 1 }}>{icon}</div>
      <p style={{ margin: 0, color: textColor, fontSize: 11, lineHeight: '15px', fontWeight: 500, flex: 1 }}>
        {useColorTags ? parseColoredText(text) : stripColorTags(text)}
      </p>
    </div>
  );
}

function CardContent({
  name,
  earnRateDescription,
  maxEarningsDescription,
  variant,
  onLearnMore,
}: Omit<TierCardProps, 'borderGradient'>) {
  const isCurrent = variant === 'current';
  const isNext = variant === 'next';
  const titleColor = isCurrent ? '#ffffff' : '#8d8aa8';
  const bodyColor = isCurrent ? '#ffffff' : '#8d8aa8';
  const ClockIcon = isCurrent ? PrimaryClockIcon : SubClockIcon;
  const GiftIcon = isCurrent ? PrimaryGiftIcon : SubGiftIcon;

  return (
    <>
      {/* Header row: tier name + pill */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          marginBottom: 6,
        }}
      >
        <p style={{ margin: 0, color: titleColor, fontSize: 17, lineHeight: 1.15, fontWeight: 700 }}>
          {name}
        </p>
        {isCurrent ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 9999,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              fontWeight: 500,
              fontSize: 10,
              lineHeight: '12px',
              background: '#fbbf24',
              color: '#000000',
              padding: '3px 8px',
            }}
          >
            Your tier
          </span>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 9999,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              fontWeight: 500,
              fontSize: 10,
              lineHeight: '12px',
              background: '#190D42',
              color: '#8d8aa8',
              padding: '3px 8px',
            }}
          >
            Locked
          </span>
        )}
      </div>

      {/* Benefit lines */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        <BenefitLine icon={<ClockIcon size={22} />} text={earnRateDescription} textColor={bodyColor} useColorTags={isCurrent} />
        <BenefitLine icon={<GiftIcon size={22} />} text={maxEarningsDescription} textColor={bodyColor} useColorTags={isCurrent} />
      </div>

      {/* "How to reach..." button — only for next tier */}
      {isNext && onLearnMore && (
        <button
          type="button"
          onClick={onLearnMore}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: '100%',
            minHeight: 30,
            marginTop: 8,
            padding: '0 12px',
            borderRadius: 9999,
            border: 'none',
            background: '#3f3564',
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          {LOCK_ICON}
          How to reach {name}
        </button>
      )}
    </>
  );
}

export const TierCard = ({
  name,
  earnRateDescription,
  maxEarningsDescription,
  variant,
  borderGradient,
  onLearnMore,
}: TierCardProps) => {
  const isCurrent = variant === 'current';

  if (isCurrent && borderGradient) {
    return (
      <div
        style={{
          width: '100%',
          padding: 2,
          borderRadius: 10,
          background: borderGradient,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            borderRadius: 8,
            background: '#0b0618',
            position: 'relative',
            overflow: 'hidden',
            padding: 10,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: AMBIENT_GRADIENT_CURRENT,
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <CardContent
              name={name}
              earnRateDescription={earnRateDescription}
              maxEarningsDescription={maxEarningsDescription}
              variant={variant}
              onLearnMore={onLearnMore}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        padding: 10,
        borderRadius: 8,
        border: '1px solid rgba(141,138,168,0.25)',
        background: '#0b0618',
        boxSizing: 'border-box',
      }}
    >
      <CardContent
        name={name}
        earnRateDescription={earnRateDescription}
        maxEarningsDescription={maxEarningsDescription}
        variant={variant}
        onLearnMore={onLearnMore}
      />
    </div>
  );
};
